import { Injectable } from '@angular/core';
import { ApiConnectionsModel, ApiConnectionsRepository, WorkspaceRepository } from '@database/central';
import { DataRepository } from '@database/workspace';
import { PrimaryKeyValueModel } from '@shared';
import { combineLatest, Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
//todo: rename it after adding more methods here
export class BusinessLayerService {
	public constructor(
		private workspaceRepository: WorkspaceRepository,
		private dataRepository: DataRepository,
		private apiConnectionsRepository: ApiConnectionsRepository
	) {}

	public getPrimaryKeyAndValue$(
		workfileId: string,
		projectNumber: string,
		tableName: string,
		entryId: number
	): Observable<PrimaryKeyValueModel[]> {
		return this.getPrimaryKey$(workfileId, tableName).pipe(
			switchMap((primaryKeys: string[]) =>
				this.dataRepository
					.getTablePrimaryKey$(tableName, projectNumber, entryId, primaryKeys)
					.pipe(map((primaryKeyValue: PrimaryKeyValueModel[]) => primaryKeyValue))
			)
		);
	}

	public getPrimaryKey$(workfileId: string, table: string): Observable<string[]> {
		return this.workspaceRepository
			.getEntityPrimaryKey$(workfileId, table)
			.pipe(map((primaryKeys: string[]) => primaryKeys));
	}

	public getPrimaryKeys$(workfileId: string, tablesName: string[]): Observable<{ [key: string]: string[] }> {
		const tablePrimaryKeyPair = {};
		const queries = [];
		for (const tableName of tablesName) {
			queries.push(
				this.getPrimaryKey$(workfileId, tableName).pipe(
					map((primaryKeyValue: string[]) => ({
						...tablePrimaryKeyPair,
						[tableName]: primaryKeyValue
					}))
				)
			);
		}

		return combineLatest(queries).pipe(
			map((primaryKeyPair: { [key: string]: string[] }[]) =>
				primaryKeyPair.reduce(
					(acc, curr) => ({ ...acc, [Object.entries(curr)[0][0]]: Object.entries(curr)[0][1] }),
					{}
				)
			)
		);
	}

	public flattenPrimaryKeysAndValues$(
		workfileId: string,
		projectNumber: string,
		tableName: string,
		entryId: number
	): Observable<string> {
		return this.getPrimaryKeyAndValue$(workfileId, projectNumber, tableName, entryId).pipe(
			map((primaryKeyValue: PrimaryKeyValueModel[]) => {
				const primaryKeysAndValues = [];

				primaryKeyValue.forEach((item: PrimaryKeyValueModel) => {
					primaryKeysAndValues.push(`${item.primaryKey}-${item.primaryKeyValue}`);
				});

				return primaryKeysAndValues.join('|');
			})
		);
	}

	public getConnectionModel$(workfileId: string): Observable<ApiConnectionsModel> {
		return this.workspaceRepository.getConnectionId$(workfileId).pipe(
			switchMap((connectionId: string) =>
				this.apiConnectionsRepository.getConnectionDetails$(connectionId).pipe(
					map((result) => {
						if (!result) {
							return null;
						}

						return result;
					})
				)
			),
			first()
		);
	}

	public isNoApiModeEnabled$(): Observable<boolean> {
		return this.apiConnectionsRepository
			.getConnections$()
			.pipe(
				map((apiConnections: ApiConnectionsModel[] | null) => !apiConnections || apiConnections.length === 0)
			);
	}
}
